import { cx } from '../lib/cx.ts';
import { Chip } from './Chip.tsx';

type ChipOption<T extends string> = {
  value: T;
  label: string;
};

type SingleProps<T extends string> = {
  label: string;
  options: readonly ChipOption<T>[];
  multiple?: false;
  value: T | null;
  onChange: (value: T | null) => void;
  className?: string;
};

type MultiProps<T extends string> = {
  label: string;
  options: readonly ChipOption<T>[];
  multiple: true;
  value: readonly T[];
  onChange: (value: T[]) => void;
  className?: string;
};

type ChipGroupProps<T extends string> = SingleProps<T> | MultiProps<T>;

/**
 * A row of chips. Single-select behaves as a radiogroup; multi-select is a
 * group of independent toggles, each carrying its own pressed state.
 *
 * Single-select is deselectable by tapping the chosen chip again — every chip
 * field in this app is optional unless DATA_MODEL.md says otherwise.
 */
export function ChipGroup<T extends string>(props: ChipGroupProps<T>) {
  const { label, options, className } = props;

  return (
    <div
      role={props.multiple === true ? 'group' : 'radiogroup'}
      aria-label={label}
      className={cx('flex flex-wrap gap-2', className)}
    >
      {options.map((option) => {
        if (props.multiple === true) {
          const selected = props.value.includes(option.value);

          return (
            <Chip
              key={option.value}
              pressed={selected}
              selected={selected}
              onClick={() => {
                props.onChange(
                  selected
                    ? props.value.filter((v) => v !== option.value)
                    : [...props.value, option.value],
                );
              }}
            >
              {option.label}
            </Chip>
          );
        }

        const selected = props.value === option.value;

        return (
          <Chip
            key={option.value}
            pressed={selected}
            selected={selected}
            onClick={() => props.onChange(selected ? null : option.value)}
          >
            {option.label}
          </Chip>
        );
      })}
    </div>
  );
}
